import { Box, DrawerHeader, Stack, Tooltip } from '@chakra-ui/react';
import { Formik } from 'formik';
import { Children, useState } from 'react';
import { useSelector } from 'react-redux';

import FieldToolTip from 'components/forms/new/FieldToolTip';
import SectionHandler from 'components/forms/new/SectionHandler';
import Drawer from 'components/presentational/Drawer';
import { Button, Card } from 'components/v4';
import Comments from 'pages/telephony/numbers/new/drawers/formFields/Comments';
import SelectField from 'pages/telephony/numbers/new/drawers/formFields/SelectField';
import ContactField from './formFields/ContactField';
import CancelButton from 'components/tables/CancelButton';

import {
    noBreadcrumbStyle,
    paddingInputStyle,
    formErrorStyle,
} from 'components/forms/new/formStyles';
import useRequestNewNumberBlock from 'pages/telephony/numbers/queries/useRequestNewNumberBlock';
import {
    blockSizeEnum,
    initialFormState,
    initialSelectState,
    quantityEnum,
} from 'pages/telephony/numbers/requestBlock/constants';
import walkthroughIds from 'pages/telephony/numbers/walkthroughIds';

const RequestBlockDrawer = ({ isOpen, onClose }) => {
    const [submitData, setSubmitData] = useState(initialFormState);
    const [stateError] = useState(initialSelectState);
    const [selectState, setSelectState] = useState(initialSelectState);
    const [isHovered, setIsHovered] = useState(false);
    const { darkMode, colorScheme } = useSelector((state) => state.settings);
    const drawerHeaderStyle = { ...noBreadcrumbStyle, ...paddingInputStyle };

    const sideDrawer = true;
    const isDisabled = !selectState.blockSize || !selectState.quantity;
    const { requestNewBlock } = walkthroughIds;
    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });

    const handleClose = () => {
        setSubmitData(initialFormState);
        setSelectState(initialSelectState);
        onClose();
    };

    const { mutate: requestNewNumberBlock } = useRequestNewNumberBlock(
        submitData,
        currentCompany,
        handleClose,
    );

    const finalFormSubmit = (data) => {
        setSubmitData({
            ...data,
            blockSize: selectState.blockSize,
            quantity: selectState.quantity,
        });
        setTimeout(() => {
            requestNewNumberBlock();
        }, 0);
    };

    const formFields = (errors, touched, values, props) => {
        const formProps = { darkMode, errors, touched, values, props };
        const fields = [
            <Box key="contact">
                <ContactField
                    isRequired={false}
                    key="contactname"
                    label="Contact Name"
                    field="contactName"
                    tooltipLabel="Name of Contact"
                    state={values}
                    onChange={props?.handleChange}
                    walkthroughId={requestNewBlock.contactName}
                    {...formProps}
                />
                <ContactField
                    isRequired={false}
                    key="contactphone"
                    label="Contact Phone"
                    field="contactPhone"
                    tooltipLabel="Phone Number of Contact"
                    state={values}
                    onChange={props?.handleChange}
                    walkthroughId={requestNewBlock.contactPhone}
                    {...formProps}
                />
                <ContactField
                    isRequired={false}
                    key="contactemail"
                    label="Contact Email"
                    field="contactEmail"
                    tooltipLabel="Email of Contact"
                    state={values}
                    onChange={props?.handleChange}
                    walkthroughId={requestNewBlock.contactEmail}
                    {...formProps}
                />
            </Box>,
            <Box key="blockquantity" sx={formErrorStyle}>
                <SelectField
                    key="blocksize"
                    field="blockSize"
                    darkMode={darkMode}
                    colorScheme={colorScheme}
                    options={blockSizeEnum}
                    state={selectState}
                    stateError={stateError}
                    onChange={(p) =>
                        setSelectState((prev) => ({
                            ...prev,
                            blockSize: p['value'],
                        }))
                    }
                    tooltipLabel="Numbers are ordered in blocks of 1, 10 or 100"
                    walkthoughId={requestNewBlock.blockSize}>
                    Block Size
                </SelectField>
                <SelectField
                    key="quantity"
                    field="quantity"
                    darkMode={darkMode}
                    colorScheme={colorScheme}
                    options={quantityEnum}
                    state={selectState}
                    stateError={stateError}
                    onChange={(p) =>
                        setSelectState((prev) => ({
                            ...prev,
                            quantity: p['value'],
                        }))
                    }
                    tooltipLabel="How many number blocks of the chosen size do you require?"
                    walkthoughId={requestNewBlock.quantity}>
                    Quantity
                </SelectField>
            </Box>,
            <Box key="country-address">
                <ContactField
                    isRequired={false}
                    key="country"
                    label="Country"
                    field="country"
                    tooltipLabel="Country of numbers required"
                    state={values}
                    onChange={props?.handleChange}
                    walkthroughId={requestNewBlock.country}
                    {...formProps}
                />
                <ContactField
                    isRequired={false}
                    key="region"
                    label="Region"
                    field="region"
                    tooltipLabel="Region of numbers required"
                    state={values}
                    onChange={props?.handleChange}
                    walkthroughId={requestNewBlock.region}
                    {...formProps}
                />
                <ContactField
                    isRequired={false}
                    key="street"
                    label="Street"
                    field="street"
                    tooltipLabel="Street Address of numbers required"
                    state={values}
                    onChange={props?.handleChange}
                    walkthroughId={requestNewBlock.street}
                    {...formProps}
                />
                <ContactField
                    isRequired={false}
                    key="suburb"
                    label="Suburb"
                    field="suburb"
                    tooltipLabel="Suburb of area of numbers required"
                    state={values}
                    onChange={props?.handleChange}
                    walkthroughId={requestNewBlock.suburb}
                    {...formProps}
                />
                <ContactField
                    isRequired={false}
                    key="state"
                    label="State"
                    field="state"
                    tooltipLabel="State of numbers required"
                    state={values}
                    onChange={props?.handleChange}
                    walkthroughId={requestNewBlock.state}
                    {...formProps}
                />
                <ContactField
                    isRequired={false}
                    key="postcode"
                    label="Postcode"
                    field="postcode"
                    tooltipLabel="Postcode of numbers required"
                    state={values}
                    onChange={props?.handleChange}
                    walkthroughId={requestNewBlock.postcode}
                    {...formProps}
                />
            </Box>,
            <Comments
                key="comments"
                hasDescription
                darkMode={darkMode}
                state={submitData?.comments}
                onChange={(e) =>
                    setSubmitData({ ...values, comments: e.target.value })
                }
                walkthroughId={requestNewBlock.comments}
            />,
        ];
        return fields;
    };

    return (
        <Drawer isOpen={isOpen} onClose={onClose}>
            <DrawerHeader pt={0} sx={drawerHeaderStyle}>
                Request New Numbers
            </DrawerHeader>
            <Formik
                initialValues={submitData}
                enableReinitialize
                onSubmit={(formData) => finalFormSubmit(formData)}>
                {({ errors, touched, handleSubmit, values, ...props }) => (
                    <form onSubmit={handleSubmit}>
                        <Stack spacing={30}>
                            <Card
                                borderRadius={15}
                                padding="0px 30px"
                                maxWidth={895}
                                ml={0}
                                bg={darkMode ? 'dark.tcap' : null}>
                                <Stack sx={{ margin: '0 !important' }}>
                                    {Children.map(
                                        formFields(
                                            errors,
                                            touched,
                                            values,
                                            props,
                                        ),
                                        (child, index) => (
                                            <SectionHandler
                                                {...{
                                                    index,
                                                    child,
                                                    count: formFields()?.length,
                                                }}
                                            />
                                        ),
                                    )}
                                </Stack>
                            </Card>

                            <Stack
                                direction="row"
                                sx={{
                                    paddingBottom: sideDrawer ? 3 : 10,
                                    marginLeft: sideDrawer && 0,
                                    marginTop: sideDrawer && 25,
                                    paddingTop: sideDrawer && 3,
                                    position: sideDrawer ? 'sticky' : 'static',
                                    left: sideDrawer ? 0 : 'auto',
                                    bottom: sideDrawer ? -35 : 'auto',
                                    backgroundColor:
                                        sideDrawer &&
                                        (darkMode
                                            ? 'var(--chakra-colors-dark-bgDark)'
                                            : '#F5F5F5'),
                                    zIndex: 1,
                                    width: '100%',
                                }}>
                                <div
                                    className="edit-form-buttons"
                                    style={{
                                        border: 'none',
                                        textAlign: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <Button
                                        type="submit"
                                        data-walkthroughid="/bringExistingNumber/button/save"
                                        className="h-10 min-w-[5rem] font-semibold disabled:cursor-not-allowed"
                                        sx={{ width: 142 }}
                                        isDisabled={isDisabled}>
                                        <Tooltip
                                            hasArrow
                                            label={
                                                isDisabled
                                                    ? 'Please select block size and quantity'
                                                    : ''
                                            }
                                            aria-label="disabled tooltip for request new numbers submit button">
                                            <span>Save</span>
                                        </Tooltip>
                                    </Button>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        flex: 1,
                                    }}
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}>
                                    <CancelButton onClick={handleClose} />
                                    {isHovered && (
                                        <FieldToolTip
                                            label="Close will clear the form"
                                            ariaLabel="close-button-tip"
                                            left="30px"
                                            top="13px"
                                        />
                                    )}
                                </div>
                            </Stack>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Drawer>
    );
};

export default RequestBlockDrawer;
